import { ReactElement, useState, useMemo } from 'react';
import { Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { AdminReportHeatmapCoords } from '~services/admin/report/types';
import { getReportHeatmap } from '~services/admin/report';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { useRequest } from '~hooks/useRequest';
import { getFeatureAvailableToDate } from '~utils/feature';
import AccessFeature from '~components/AccessFeature';
import LoadingFallback from '~components/LoadingFallback';

import ReportsHeatmapSectionNotVisits from './NotVisits';
import ReportsHeatmapSectionMap from './Map';

const ReportsHeatmapSection = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [visits, setVisits] = useState<AdminReportHeatmapCoords[]>([]);

  const isValidGetVisits = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.ReportHeatmap,
        getFeatureAvailableToDate(PlanFeaturesTypes.ReportHeatmap),
      ),
    [hasAccessToFeature],
  );

  const [, loadingVisits] = useRequest(
    {
      request: getReportHeatmap,
      payload: null,
      initialLoading: isValidGetVisits,
      isValidToRequest: isValidGetVisits,
      withPostSuccess: (response) => {
        const currentVisits = response.data?.data as AdminReportHeatmapCoords[];

        setVisits(currentVisits);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
    },
    [],
  );

  const hasVisits = useMemo(() => visits.length > 0, [visits]);

  return (
    <Stack spacing={2} flex={1} height="100%">
      <AccessFeature.Alert
        validFeature={PlanFeaturesTypes.ReportHeatmap}
        availableToDate={getFeatureAvailableToDate(
          PlanFeaturesTypes.ReportHeatmap,
        )}
      />

      <AccessFeature.Hidden
        validFeature={PlanFeaturesTypes.ReportHeatmap}
        availableToDate={getFeatureAvailableToDate(
          PlanFeaturesTypes.ReportHeatmap,
        )}
        type="notAccess"
        validDemo
      >
        <Stack spacing={2} flex={1} height="100%">
          {loadingVisits && <LoadingFallback />}

          {!loadingVisits && (
            <>
              {!hasVisits && <ReportsHeatmapSectionNotVisits />}

              {hasVisits && <ReportsHeatmapSectionMap visits={visits} />}
            </>
          )}
        </Stack>
      </AccessFeature.Hidden>
    </Stack>
  );
};

export default ReportsHeatmapSection;
